'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, ChevronRight, ExternalLink, Receipt, CheckCircle2, RefreshCw, Plus, Trash2, CreditCard, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PaymentRecord {
  id: string;
  date: string;
  amount: number;
  method: string;
  reference: string;
  note: string;
  type: 'payment';
}

interface CreditNote {
  id: string;
  date: string;
  amount: number;
  reference: string;
  reason: string;
  type: 'credit';
}

type Transaction = PaymentRecord | CreditNote;

// ─── Data ─────────────────────────────────────────────────────────────────────

const allInvoices = [
  { id: 'inv-10482', invoiceNumber: 'INV-10482', type: 'AR' as const, jobId: 'JOB-CRM-9924', jobRef: 'job-crm-9924', jobName: 'Merch drop — TechConf 2026', counterparty: 'TechConf Events Ltd', issueDate: '10/08/2026', dueDate: '10/08/2026', amountRaw: 8400, status: 'overdue', isOverdue: true, linkedDoc: 'SO-3243', linkedDocId: 'so-3243' },
  { id: 'inv-10481', invoiceNumber: 'INV-10481', type: 'AR' as const, jobId: 'JOB-CRM-9921', jobRef: 'job-crm-9921', jobName: 'Branded hoodies — RetailCo Q3', counterparty: 'RetailCo UK', issueDate: '14/08/2026', dueDate: '22/08/2026', amountRaw: 14220, status: 'due', isOverdue: false, linkedDoc: 'SO-3238', linkedDocId: 'so-3238' },
  { id: 'inv-10480', invoiceNumber: 'INV-10480', type: 'AR' as const, jobId: 'JOB-CRM-9917', jobRef: 'job-crm-9917', jobName: 'Tote bags — GreenFest', counterparty: 'GreenFest CIC', issueDate: '01/08/2026', dueDate: '18/08/2026', amountRaw: 3150, status: 'paid', isOverdue: false, linkedDoc: 'SO-3225', linkedDocId: 'so-3225' },
  { id: 'inv-10479', invoiceNumber: 'INV-10479', type: 'AP' as const, jobId: 'JOB-CRM-9924', jobRef: 'job-crm-9924', jobName: 'Merch drop — TechConf 2026', counterparty: 'PrintFast UK', issueDate: '16/08/2026', dueDate: '30/08/2026', amountRaw: 6800, status: 'sent', isOverdue: false, linkedDoc: 'PO-2853', linkedDocId: 'po-2853' },
  { id: 'inv-10478', invoiceNumber: 'INV-10478', type: 'AR' as const, jobId: 'JOB-CRM-9915', jobRef: 'job-crm-9915', jobName: 'Embroidered polos — HealthOrg', counterparty: 'HealthOrg NHS', issueDate: '08/08/2026', dueDate: '28/08/2026', amountRaw: 11640, status: 'sent', isOverdue: false, linkedDoc: 'SO-3218', linkedDocId: 'so-3218' },
  { id: 'inv-10477', invoiceNumber: 'INV-10477', type: 'AP' as const, jobId: 'JOB-CRM-9921', jobRef: 'job-crm-9921', jobName: 'Branded hoodies — RetailCo Q3', counterparty: 'Gildan Activewear EU', issueDate: '14/08/2026', dueDate: '14/08/2026', amountRaw: 9600, status: 'overdue', isOverdue: true, linkedDoc: 'PO-2848', linkedDocId: 'po-2848' },
  { id: 'inv-10476', invoiceNumber: 'INV-10476', type: 'AR' as const, jobId: 'JOB-CRM-9912', jobRef: 'job-crm-9912', jobName: 'Caps and beanies — SportsBrand', counterparty: 'SportsBrand Ltd', issueDate: '04/08/2026', dueDate: '04/08/2026', amountRaw: 6800, status: 'overdue', isOverdue: true, linkedDoc: 'SO-3211', linkedDocId: 'so-3211' },
  { id: 'inv-10475', invoiceNumber: 'INV-10475', type: 'AP' as const, jobId: 'JOB-CRM-9915', jobRef: 'job-crm-9915', jobName: 'Embroidered polos — HealthOrg', counterparty: 'Gildan Activewear EU', issueDate: '06/08/2026', dueDate: '20/08/2026', amountRaw: 14200, status: 'paid', isOverdue: false, linkedDoc: 'PO-2835', linkedDocId: 'po-2835' },
  { id: 'inv-10474', invoiceNumber: 'INV-10474', type: 'AR' as const, jobId: 'JOB-CRM-9908', jobRef: 'job-crm-9908', jobName: 'Desk kits — FinanceCorp', counterparty: 'FinanceCorp PLC', issueDate: '29/07/2026', dueDate: '12/08/2026', amountRaw: 11760, status: 'reconciled', isOverdue: false, linkedDoc: 'SO-3201', linkedDocId: 'so-3201' },
  { id: 'inv-10473', invoiceNumber: 'INV-10473', type: 'AP' as const, jobId: 'JOB-CRM-9919', jobRef: 'job-crm-9919', jobName: 'Promo kit — SaaS Summit', counterparty: 'Moleskine Trade', issueDate: '12/08/2026', dueDate: '26/08/2026', amountRaw: 9400, status: 'sent', isOverdue: false, linkedDoc: 'PO-2843', linkedDocId: 'po-2843' },
  { id: 'inv-10472', invoiceNumber: 'INV-10472', type: 'AR' as const, jobId: 'JOB-CRM-9904', jobRef: 'job-crm-9904', jobName: 'Branded water bottles — FitLife', counterparty: 'FitLife Gyms', issueDate: '23/07/2026', dueDate: '06/08/2026', amountRaw: 8640, status: 'reconciled', isOverdue: false, linkedDoc: 'SO-3196', linkedDocId: 'so-3196' },
  { id: 'inv-10471', invoiceNumber: 'INV-10471', type: 'AP' as const, jobId: 'JOB-CRM-9912', jobRef: 'job-crm-9912', jobName: 'Caps and beanies — SportsBrand', counterparty: 'Headwear Direct', issueDate: '02/08/2026', dueDate: '16/08/2026', amountRaw: 7800, status: 'paid', isOverdue: false, linkedDoc: 'PO-2831', linkedDocId: 'po-2831' },
];

const invoiceLineItems: Record<string, { id: string; description: string; qty: number; unitPrice: number; total: number }[]> = {
  'inv-10482': [
    { id: 'li-inv10482-1', description: 'Lanyards — dye-sub print, 1000 units', qty: 1000, unitPrice: 6.80, total: 6800 },
    { id: 'li-inv10482-2', description: 'Artwork setup and proofing', qty: 1, unitPrice: 800, total: 800 },
    { id: 'li-inv10482-3', description: 'Express delivery — next day', qty: 1, unitPrice: 800, total: 800 },
  ],
  'inv-10478': [
    { id: 'li-inv10478-1', description: 'Gildan 64000 polo — embroidered NHS logo — assorted sizes', qty: 450, unitPrice: 22.40, total: 10080 },
    { id: 'li-inv10478-2', description: 'Embroidery setup — 5 trust variants', qty: 5, unitPrice: 144, total: 720 },
    { id: 'li-inv10478-3', description: 'Packing and labelling per NHS spec', qty: 1, unitPrice: 840, total: 840 },
  ],
  'inv-10481': [
    { id: 'li-inv10481-1', description: 'Gildan 18500 hoodie navy — embroidered logo — assorted sizes', qty: 250, unitPrice: 52.80, total: 13200 },
    { id: 'li-inv10481-2', description: 'Embroidery setup charge', qty: 1, unitPrice: 1020, total: 1020 },
  ],
  'inv-10474': [
    { id: 'li-inv10474-1', description: 'Desk kit bundle — notebook, pen, USB hub', qty: 240, unitPrice: 49, total: 11760 },
  ],
  'inv-10480': [
    { id: 'li-inv10480-1', description: 'Organic cotton tote bag — screen printed logo', qty: 500, unitPrice: 5.80, total: 2900 },
    { id: 'li-inv10480-2', description: 'Screen print setup', qty: 1, unitPrice: 250, total: 250 },
  ],
};

const defaultLineItems = [
  { id: 'li-default-1', description: 'Branded merchandise — see linked document for detail', qty: 1, unitPrice: 0, total: 0 },
];

const seedTransactions: Record<string, Transaction[]> = {
  'inv-10480': [
    { id: 'pay-480-1', date: '18/08/2026', amount: 3150, method: 'BACS transfer', reference: 'PAY-4821', note: 'Full payment received', type: 'payment' },
  ],
  'inv-10475': [
    { id: 'pay-475-1', date: '20/08/2026', amount: 14200, method: 'BACS transfer', reference: 'PAY-4819', note: 'Full payment made', type: 'payment' },
  ],
  'inv-10474': [
    { id: 'pay-474-1', date: '10/08/2026', amount: 11760, method: 'BACS transfer', reference: 'PAY-4810', note: 'Full payment received', type: 'payment' },
  ],
  'inv-10472': [
    { id: 'pay-472-1', date: '04/08/2026', amount: 8640, method: 'BACS transfer', reference: 'PAY-4802', note: 'Full payment received', type: 'payment' },
  ],
  'inv-10471': [
    { id: 'pay-471-1', date: '16/08/2026', amount: 7800, method: 'BACS transfer', reference: 'PAY-4816', note: 'Full payment made', type: 'payment' },
  ],
  'inv-10482': [
    { id: 'pay-482-1', date: '01/08/2026', amount: 4000, method: 'BACS transfer', reference: 'PAY-4800', note: 'Part payment — 50% deposit', type: 'payment' },
  ],
};

const invoiceStatuses = ['draft', 'sent', 'due', 'overdue', 'paid', 'reconciled'];

const paymentMethods = ['BACS transfer', 'CHAPS', 'Faster Payments', 'Cheque', 'Card', 'Direct Debit', 'Other'];

const fmt = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function InvoiceLandingContent({ invoiceId }: { invoiceId: string }) {
  const invoice = allInvoices.find(i => i.id === invoiceId);
  const [currentStatus, setCurrentStatus] = useState(invoice?.status ?? 'sent');
  const [transactions, setTransactions] = useState<Transaction[]>(seedTransactions[invoiceId] ?? []);
  const [activeTab, setActiveTab] = useState<'payments' | 'credit-notes' | 'line-items'>('payments');
  const [notes, setNotes] = useState('');

  // Payment form state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('BACS transfer');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentNote, setPaymentNote] = useState('');

  // Credit note form state
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [creditDate, setCreditDate] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditRef, setCreditRef] = useState('');
  const [creditReason, setCreditReason] = useState('');

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-4">
        <Receipt size={40} style={{ color: 'var(--border)' }} />
        <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Invoice not found</p>
        <p className="text-sm" style={{ color: 'var(--slate)' }}>No invoice with ID &quot;{invoiceId}&quot; exists.</p>
        <Link href="/invoice-management" className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--accent)' }}>
          <ArrowLeft size={14} /> Back to Invoices
        </Link>
      </div>
    );
  }

  const lineItems = invoiceLineItems[invoice.id] ?? defaultLineItems;
  const invoiceTotal = invoice.amountRaw;
  const totalPaid = transactions.filter(t => t.type === 'payment').reduce((s, t) => s + t.amount, 0);
  const totalCredits = transactions.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0);
  const outstanding = Math.max(0, invoiceTotal - totalPaid - totalCredits);
  const isFullySettled = outstanding === 0;

  const handleAddPayment = () => {
    if (!paymentDate || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid date and amount');
      return;
    }
    const newPayment: PaymentRecord = {
      id: `pay-${Date.now()}`,
      date: paymentDate,
      amount: parseFloat(paymentAmount),
      method: paymentMethod,
      reference: paymentRef || `PAY-${Math.floor(Math.random() * 9000) + 1000}`,
      note: paymentNote,
      type: 'payment',
    };
    setTransactions(prev => [...prev, newPayment]);
    toast.success(`Payment of ${fmt(newPayment.amount)} recorded`);
    setShowPaymentForm(false);
    setPaymentDate(''); setPaymentAmount(''); setPaymentRef(''); setPaymentNote('');
    setPaymentMethod('BACS transfer');
    if (parseFloat(paymentAmount) >= outstanding) {
      setCurrentStatus('paid');
    }
  };

  const handleAddCreditNote = () => {
    if (!creditDate || !creditAmount || parseFloat(creditAmount) <= 0) {
      toast.error('Please enter a valid date and amount');
      return;
    }
    const newCredit: CreditNote = {
      id: `cn-${Date.now()}`,
      date: creditDate,
      amount: parseFloat(creditAmount),
      reference: creditRef || `CN-${Math.floor(Math.random() * 9000) + 1000}`,
      reason: creditReason,
      type: 'credit',
    };
    setTransactions(prev => [...prev, newCredit]);
    toast.success(`Credit note of ${fmt(newCredit.amount)} applied`);
    setShowCreditForm(false);
    setCreditDate(''); setCreditAmount(''); setCreditRef(''); setCreditReason('');
  };

  const handleRemoveTransaction = (id: string) => {
    setTransactions(prev => prev.filter(t => t.id !== id));
    toast.success('Transaction removed');
  };

  const handleStatusChange = (status: string) => {
    setCurrentStatus(status);
    toast.success(`Status updated to ${status}`);
  };

  const payments = transactions.filter(t => t.type === 'payment') as PaymentRecord[];
  const creditNotes = transactions.filter(t => t.type === 'credit') as CreditNote[];

  const linkedPath = invoice.type === 'AR'
    ? `/sales-orders/${invoice.linkedDocId}`
    : `/purchase-orders/${invoice.linkedDocId}`;

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--slate)' }}>
        <Link href="/invoice-management" className="hover:underline flex items-center gap-1" style={{ color: 'var(--accent)' }}>
          <ArrowLeft size={12} /> Invoices
        </Link>
        <ChevronRight size={12} />
        <span style={{ color: 'var(--foreground)' }}>{invoice.invoiceNumber}</span>
      </nav>

      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>{invoice.invoiceNumber}</h1>
            <span
              className="text-xs font-bold px-2 py-0.5 rounded"
              style={{
                backgroundColor: invoice.type === 'AR' ? 'var(--teal-light)' : 'var(--slate-light)',
                color: invoice.type === 'AR' ? 'var(--accent)' : 'var(--slate)',
              }}
            >
              {invoice.type === 'AR' ? 'Accounts Receivable' : 'Accounts Payable'}
            </span>
            <StatusBadge status={currentStatus as never} pulse={invoice.isOverdue && currentStatus === 'overdue'} />
          </div>
          <p className="text-sm" style={{ color: 'var(--slate)' }}>{invoice.counterparty} · {invoice.jobName}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href={linkedPath}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border transition-all"
            style={{ borderColor: 'var(--border)', color: 'var(--accent)', backgroundColor: 'var(--card)' }}
          >
            <ExternalLink size={12} />
            {invoice.linkedDoc}
          </Link>
        </div>
      </div>

      {/* Overdue alert */}
      {invoice.isOverdue && currentStatus === 'overdue' && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-lg border"
          style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}
        >
          <AlertCircle size={16} style={{ color: '#DC2626' }} />
          <p className="text-sm font-medium" style={{ color: '#DC2626' }}>
            This invoice is overdue — payment required immediately
          </p>
        </div>
      )}

      {/* Balance summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Invoice total', value: fmt(invoiceTotal), color: 'var(--foreground)', sub: '' },
          { label: 'Total paid', value: fmt(totalPaid), color: '#059669', sub: `${payments.length} payment${payments.length !== 1 ? 's' : ''}` },
          { label: 'Credit notes', value: fmt(totalCredits), color: '#7C3AED', sub: `${creditNotes.length} note${creditNotes.length !== 1 ? 's' : ''}` },
          { label: 'Outstanding', value: fmt(outstanding), color: outstanding > 0 ? '#DC2626' : '#059669', sub: isFullySettled ? 'Fully settled' : 'Balance due' },
        ].map(s => (
          <div
            key={`bal-${s.label}`}
            className="rounded-lg border px-4 py-3"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--slate)' }}>{s.label}</p>
            <p className="text-xl font-bold tabular-nums" style={{ color: s.color }}>{s.value}</p>
            {s.sub && <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>{s.sub}</p>}
          </div>
        ))}
      </div>

      {/* Progress bar */}
      {invoiceTotal > 0 && (
        <div
          className="rounded-lg border px-4 py-3"
          style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium" style={{ color: 'var(--slate)' }}>Payment progress</p>
            <p className="text-xs font-semibold tabular-nums" style={{ color: 'var(--foreground)' }}>
              {Math.min(100, Math.round(((totalPaid + totalCredits) / invoiceTotal) * 100))}% settled
            </p>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(100, ((totalPaid + totalCredits) / invoiceTotal) * 100)}%`,
                backgroundColor: isFullySettled ? '#059669' : 'var(--accent)',
              }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Invoice details + status */}
        <div className="space-y-4">
          {/* Invoice details card */}
          <div
            className="rounded-xl border p-4 space-y-3"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Invoice details</h3>
            {[
              { label: invoice.type === 'AR' ? 'Client' : 'Supplier', value: invoice.counterparty },
              { label: 'Job', value: invoice.jobId },
              { label: 'Issue date', value: invoice.issueDate },
              { label: 'Due date', value: invoice.dueDate },
              { label: invoice.type === 'AR' ? 'Linked SO' : 'Linked PO', value: invoice.linkedDoc, isLink: true, href: linkedPath },
            ].map(f => (
              <div key={`inv-det-${f.label}`} className="flex items-start justify-between gap-2">
                <p className="text-xs" style={{ color: 'var(--slate)' }}>{f.label}</p>
                {f.isLink ? (
                  <Link href={f.href!} className="text-xs font-semibold flex items-center gap-1" style={{ color: 'var(--accent)' }}>
                    {f.value} <ExternalLink size={10} />
                  </Link>
                ) : (
                  <p className="text-xs font-semibold text-right" style={{ color: 'var(--foreground)' }}>{f.value}</p>
                )}
              </div>
            ))}
          </div>

          {/* Status management */}
          <div
            className="rounded-xl border p-4"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--foreground)' }}>Update status</h3>
            <div className="flex flex-wrap gap-2">
              {invoiceStatuses.map(s => (
                <button
                  key={`status-btn-${s}`}
                  onClick={() => handleStatusChange(s)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border transition-all"
                  style={{
                    backgroundColor: currentStatus === s ? 'var(--ink)' : 'var(--cloud)',
                    color: currentStatus === s ? 'white' : 'var(--slate)',
                    borderColor: currentStatus === s ? 'var(--ink)' : 'var(--border)',
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            {currentStatus === 'paid' && (
              <button
                onClick={() => { handleStatusChange('reconciled'); toast.success('Invoice reconciled'); }}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white transition-all"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                <RefreshCw size={13} /> Reconcile invoice
              </button>
            )}
          </div>

          {/* Notes */}
          <div
            className="rounded-xl border p-4"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--foreground)' }}>Notes</h3>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Payment terms, disputes, client communications…"
              rows={4}
              className="w-full px-3 py-2.5 text-xs rounded-md border outline-none resize-none"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            />
            <button
              onClick={() => toast.success('Notes saved')}
              className="mt-2 w-full px-3 py-2 text-xs font-medium rounded-md border transition-all"
              style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--cloud)' }}
            >
              Save notes
            </button>
          </div>
        </div>

        {/* Right: Tabs — Payments / Credit Notes / Line Items */}
        <div className="lg:col-span-2">
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            {/* Tab header */}
            <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
              {([
                { key: 'payments', label: `Payments (${payments.length})`, icon: CreditCard },
                { key: 'credit-notes', label: `Credit Notes (${creditNotes.length})`, icon: FileText },
                { key: 'line-items', label: 'Line Items', icon: Receipt },
              ] as const).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors"
                  style={{
                    borderColor: activeTab === tab.key ? 'var(--accent)' : 'transparent',
                    color: activeTab === tab.key ? 'var(--accent)' : 'var(--slate)',
                  }}
                >
                  <tab.icon size={13} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── Payments tab ── */}
            {activeTab === 'payments' && (
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs" style={{ color: 'var(--slate)' }}>
                    {invoice.type === 'AR' ? 'Payments received from client' : 'Payments made to supplier'}
                  </p>
                  <button
                    onClick={() => { setShowPaymentForm(true); setShowCreditForm(false); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-white transition-all"
                    style={{ backgroundColor: 'var(--accent)' }}
                  >
                    <Plus size={12} /> Add payment
                  </button>
                </div>

                {/* Add payment form */}
                {showPaymentForm && (
                  <div
                    className="rounded-lg border p-4 space-y-3"
                    style={{ backgroundColor: 'var(--cloud)', borderColor: 'var(--accent)' }}
                  >
                    <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>Record payment</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Date *</label>
                        <input
                          type="date"
                          value={paymentDate}
                          onChange={e => setPaymentDate(e.target.value)}
                          className="w-full px-3 py-2 text-xs rounded-md border outline-none"
                          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Amount (£) *</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={`Max ${fmt(outstanding)}`}
                          value={paymentAmount}
                          onChange={e => setPaymentAmount(e.target.value)}
                          className="w-full px-3 py-2 text-xs rounded-md border outline-none"
                          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Payment method</label>
                        <select
                          value={paymentMethod}
                          onChange={e => setPaymentMethod(e.target.value)}
                          className="w-full px-3 py-2 text-xs rounded-md border outline-none appearance-none"
                          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                        >
                          {paymentMethods.map(m => <option key={m}>{m}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Reference</label>
                        <input
                          type="text"
                          placeholder="e.g. PAY-4821"
                          value={paymentRef}
                          onChange={e => setPaymentRef(e.target.value)}
                          className="w-full px-3 py-2 text-xs rounded-md border outline-none"
                          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Note</label>
                      <input
                        type="text"
                        placeholder="e.g. Part payment — 50% deposit"
                        value={paymentNote}
                        onChange={e => setPaymentNote(e.target.value)}
                        className="w-full px-3 py-2 text-xs rounded-md border outline-none"
                        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddPayment}
                        className="flex-1 py-2 text-xs font-semibold rounded-md text-white transition-all"
                        style={{ backgroundColor: 'var(--accent)' }}
                      >
                        Save payment
                      </button>
                      <button
                        onClick={() => setShowPaymentForm(false)}
                        className="px-4 py-2 text-xs font-medium rounded-md border transition-all"
                        style={{ borderColor: 'var(--border)', color: 'var(--slate)', backgroundColor: 'var(--card)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Payment list */}
                {payments.length === 0 ? (
                  <div
                    className="rounded-lg border px-4 py-8 text-center"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)' }}
                  >
                    <CreditCard size={24} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
                    <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>No payments recorded</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--slate)' }}>Add a payment above to track receipts against this invoice</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {payments.map(pay => (
                      <div
                        key={pay.id}
                        className="flex items-center justify-between rounded-lg border px-4 py-3"
                        style={{ backgroundColor: 'var(--teal-light)', borderColor: 'var(--accent)' }}
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle size={15} style={{ color: 'var(--accent)' }} />
                          <div>
                            <p className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>
                              {invoice.type === 'AR' ? 'Payment received' : 'Payment made'} — {pay.date}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>
                              {pay.method} · Ref: {pay.reference}
                              {pay.note && ` · ${pay.note}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="tabular-nums font-bold text-sm" style={{ color: 'var(--accent)' }}>{fmt(pay.amount)}</span>
                          <button
                            onClick={() => handleRemoveTransaction(pay.id)}
                            className="p-1 rounded transition-colors hover:bg-red-100"
                            title="Remove payment"
                          >
                            <Trash2 size={12} style={{ color: '#DC2626' }} />
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Totals row */}
                    <div
                      className="flex items-center justify-between rounded-lg border px-4 py-2.5"
                      style={{ backgroundColor: 'var(--cloud)', borderColor: 'var(--border)' }}
                    >
                      <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>Total payments</p>
                      <p className="text-sm font-bold tabular-nums" style={{ color: '#059669' }}>{fmt(totalPaid)}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Credit Notes tab ── */}
            {activeTab === 'credit-notes' && (
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs" style={{ color: 'var(--slate)' }}>Credit notes reduce the outstanding balance</p>
                  <button
                    onClick={() => { setShowCreditForm(true); setShowPaymentForm(false); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-white transition-all"
                    style={{ backgroundColor: '#7C3AED' }}
                  >
                    <Plus size={12} /> Add credit note
                  </button>
                </div>

                {/* Add credit note form */}
                {showCreditForm && (
                  <div
                    className="rounded-lg border p-4 space-y-3"
                    style={{ backgroundColor: 'var(--cloud)', borderColor: '#7C3AED' }}
                  >
                    <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>Issue credit note</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Date *</label>
                        <input
                          type="date"
                          value={creditDate}
                          onChange={e => setCreditDate(e.target.value)}
                          className="w-full px-3 py-2 text-xs rounded-md border outline-none"
                          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Amount (£) *</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Credit amount"
                          value={creditAmount}
                          onChange={e => setCreditAmount(e.target.value)}
                          className="w-full px-3 py-2 text-xs rounded-md border outline-none"
                          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Credit note ref</label>
                        <input
                          type="text"
                          placeholder="e.g. CN-1042"
                          value={creditRef}
                          onChange={e => setCreditRef(e.target.value)}
                          className="w-full px-3 py-2 text-xs rounded-md border outline-none"
                          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--slate)' }}>Reason</label>
                        <input
                          type="text"
                          placeholder="e.g. Damaged goods, pricing error"
                          value={creditReason}
                          onChange={e => setCreditReason(e.target.value)}
                          className="w-full px-3 py-2 text-xs rounded-md border outline-none"
                          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)', color: 'var(--foreground)' }}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddCreditNote}
                        className="flex-1 py-2 text-xs font-semibold rounded-md text-white transition-all"
                        style={{ backgroundColor: '#7C3AED' }}
                      >
                        Save credit note
                      </button>
                      <button
                        onClick={() => setShowCreditForm(false)}
                        className="px-4 py-2 text-xs font-medium rounded-md border transition-all"
                        style={{ borderColor: 'var(--border)', color: 'var(--slate)', backgroundColor: 'var(--card)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Credit note list */}
                {creditNotes.length === 0 ? (
                  <div
                    className="rounded-lg border px-4 py-8 text-center"
                    style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)' }}
                  >
                    <FileText size={24} className="mx-auto mb-2" style={{ color: 'var(--border)' }} />
                    <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>No credit notes issued</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--slate)' }}>Credit notes reduce the outstanding balance on this invoice</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {creditNotes.map(cn => (
                      <div
                        key={cn.id}
                        className="flex items-center justify-between rounded-lg border px-4 py-3"
                        style={{ backgroundColor: '#F5F3FF', borderColor: '#7C3AED' }}
                      >
                        <div className="flex items-center gap-3">
                          <FileText size={15} style={{ color: '#7C3AED' }} />
                          <div>
                            <p className="text-xs font-semibold" style={{ color: '#7C3AED' }}>
                              Credit note — {cn.date}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>
                              Ref: {cn.reference}
                              {cn.reason && ` · ${cn.reason}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="tabular-nums font-bold text-sm" style={{ color: '#7C3AED' }}>−{fmt(cn.amount)}</span>
                          <button
                            onClick={() => handleRemoveTransaction(cn.id)}
                            className="p-1 rounded transition-colors hover:bg-red-100"
                            title="Remove credit note"
                          >
                            <Trash2 size={12} style={{ color: '#DC2626' }} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div
                      className="flex items-center justify-between rounded-lg border px-4 py-2.5"
                      style={{ backgroundColor: 'var(--cloud)', borderColor: 'var(--border)' }}
                    >
                      <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>Total credits</p>
                      <p className="text-sm font-bold tabular-nums" style={{ color: '#7C3AED' }}>−{fmt(totalCredits)}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Line Items tab ── */}
            {activeTab === 'line-items' && (
              <div className="p-4">
                <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--cloud)', borderBottom: '1px solid var(--border)' }}>
                        {['Description', 'Qty', 'Unit price', 'Total'].map(h => (
                          <th key={`li-th-${h}`} className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider" style={{ color: 'var(--slate)', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map(li => (
                        <tr key={li.id} className="border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                          <td className="px-3 py-2.5" style={{ color: 'var(--foreground)' }}>{li.description}</td>
                          <td className="px-3 py-2.5 tabular-nums" style={{ color: 'var(--slate)' }}>{li.qty}</td>
                          <td className="px-3 py-2.5 tabular-nums" style={{ color: 'var(--slate)' }}>
                            {li.unitPrice > 0 ? fmt(li.unitPrice) : '—'}
                          </td>
                          <td className="px-3 py-2.5 tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
                            {li.total > 0 ? fmt(li.total) : '—'}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: 'var(--cloud)', borderTop: '1px solid var(--border)' }}>
                        <td colSpan={3} className="px-3 py-2.5 font-semibold text-right" style={{ color: 'var(--foreground)' }}>Invoice total</td>
                        <td className="px-3 py-2.5 tabular-nums font-bold" style={{ color: 'var(--foreground)' }}>{fmt(invoiceTotal)}</td>
                      </tr>
                      {totalCredits > 0 && (
                        <tr style={{ backgroundColor: '#F5F3FF', borderTop: '1px solid var(--border)' }}>
                          <td colSpan={3} className="px-3 py-2.5 font-semibold text-right" style={{ color: '#7C3AED' }}>Less credit notes</td>
                          <td className="px-3 py-2.5 tabular-nums font-bold" style={{ color: '#7C3AED' }}>−{fmt(totalCredits)}</td>
                        </tr>
                      )}
                      {totalPaid > 0 && (
                        <tr style={{ backgroundColor: 'var(--teal-light)', borderTop: '1px solid var(--border)' }}>
                          <td colSpan={3} className="px-3 py-2.5 font-semibold text-right" style={{ color: 'var(--accent)' }}>Less payments</td>
                          <td className="px-3 py-2.5 tabular-nums font-bold" style={{ color: 'var(--accent)' }}>−{fmt(totalPaid)}</td>
                        </tr>
                      )}
                      <tr style={{ backgroundColor: outstanding > 0 ? '#FEF2F2' : 'var(--teal-light)', borderTop: '2px solid var(--border)' }}>
                        <td colSpan={3} className="px-3 py-3 font-bold text-right" style={{ color: outstanding > 0 ? '#DC2626' : '#059669' }}>
                          {isFullySettled ? 'Fully settled' : 'Balance outstanding'}
                        </td>
                        <td className="px-3 py-3 tabular-nums font-bold text-base" style={{ color: outstanding > 0 ? '#DC2626' : '#059669' }}>
                          {fmt(outstanding)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Fully settled banner */}
          {isFullySettled && (
            <div
              className="mt-4 flex items-center gap-3 px-4 py-3 rounded-lg border"
              style={{ backgroundColor: '#ECFDF5', borderColor: '#6EE7B7' }}
            >
              <CheckCircle2 size={16} style={{ color: '#059669' }} />
              <p className="text-sm font-semibold" style={{ color: '#059669' }}>
                Invoice fully settled — all payments and credits account for the full invoice value
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
